import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogIn, UserPlus, ArrowRight, Shield, Users, FileText, Calculator, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAdvisorAuth } from '@/contexts/AdvisorAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hintergrundbild als stabile Modul-Konstante + Style-Objekt:
// verhindert, dass bei jedem Re-Render ein neues Style-Objekt erzeugt und das
// Bild neu geladen wird (Flacker-Bug beim SB-Login, Juni 2026).
const BG_IMAGE_URL = "https://images.unsplash.com/photo-1637525609391-ab1919948bbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGdyZWVuJTIwZ2VvbWV0cmljJTIwbW9kZXJuJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODEwMjg3MzF8MA&ixlib=rb-4.1.0&q=85";
const BG_STYLE = { backgroundImage: `url('${BG_IMAGE_URL}')` };

export default function AdvisorLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { advisor, loading: authLoading, login } = useAdvisorAuth();
  const initialMode = searchParams.get('mode') === 'register' ? 'register' : 'login';
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [verifyToken] = useState(searchParams.get('verify'));

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [taxNumber, setTaxNumber] = useState('');
  const [step, setStep] = useState('form'); // form | pwchange | mfa
  const [pendingToken, setPendingToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  // Hintergrundbild einmalig vorladen, damit es beim Anzeigen nicht flackert/nachlädt.
  useEffect(() => {
    const img = new Image();
    img.src = BG_IMAGE_URL;
  }, []);

  // Handle email verification token
  useEffect(() => {
    if (verifyToken) {
      axios.post(`${API}/advisor/auth/verify/${verifyToken}`)
        .then(res => { setSuccess(res.data.message); setMode('login'); })
        .catch(err => setError(err.response?.data?.detail || 'Verifizierung fehlgeschlagen.'));
    }
  }, [verifyToken]);

  // Redirect if already logged in — deklarativ (kein useEffect-Race mehr).
  // Greift erst, wenn der Auth-Check abgeschlossen ist (authLoading === false).

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/advisor/auth/login`, { email, password, portal: 'sb' });
      // Sicherheits-Stufen (Aug 2026): Erstlogin-Passwortwechsel / 2FA vor der Sitzung
      if (res.data?.status === 'password_change_required') {
        setPendingToken(res.data.pending_token); setStep('pwchange'); return;
      }
      if (res.data?.status === 'mfa_required') {
        setPendingToken(res.data.pending_token); setStep('mfa'); return;
      }
      // Session-Cookie verifizieren BEVOR wir ins Portal wechseln. Verhindert
      // das „Dashboard blitzt auf → zurück zur Login-Maske", falls das
      // httpOnly-Cookie (z.B. Subdomain/Cross-Origin) nicht mitgesendet wird.
      const me = await axios.get(`${API}/advisor/auth/me`);
      login(me.data);
      navigate('/advisor/dashboard');
    } catch (err) {
      const status = err.response?.status;
      const url = err.config?.url || '';
      if (url.includes('/advisor/auth/me') || (status === 401 && !err.response?.data?.detail)) {
        setError('Sitzung konnte nicht hergestellt werden. Bitte erlaube Cookies für diese Seite und versuche es erneut.');
      } else {
        setError(err.response?.data?.detail || 'Anmeldung fehlgeschlagen.');
      }
    } finally { setLoading(false); }
  };

  const finishSession = async () => {
    const me = await axios.get(`${API}/advisor/auth/me`);
    login(me.data);
    navigate('/advisor/dashboard');
  };

  const handleForcePwChange = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== newPassword2) { setError('Die Passwörter stimmen nicht überein.'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/advisor/auth/force-change-password`, {
        pending_token: pendingToken, current_password: password, new_password: newPassword,
      });
      if (res.data?.status === 'mfa_required') {
        setPendingToken(res.data.pending_token); setStep('mfa');
      } else {
        await finishSession();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Passwortwechsel fehlgeschlagen.');
    } finally { setLoading(false); }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/advisor/auth/2fa/verify`, { pending_token: pendingToken, code: mfaCode });
      await finishSession();
    } catch (err) {
      setError(err.response?.data?.detail || 'Code ungültig.');
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (password.length < 6) { setError('Passwort muss mindestens 6 Zeichen lang sein.'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/advisor/auth/register`, {
        email, password, name, company_name: companyName, tax_number: taxNumber,
      });
      setSuccess(res.data.message);
      setMode('login');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registrierung fehlgeschlagen.');
    } finally { setLoading(false); }
  };

  const inputCls = "mt-1.5 rounded-xl border-slate-200 focus-visible:ring-emerald-500 h-11";

  // Bereits eingeloggt → deklarativ zum Dashboard (nur nach abgeschlossenem Check).
  if (!authLoading && advisor) {
    return <Navigate to="/advisor/dashboard" replace />;
  }

  return (
    <div className="min-h-screen flex bg-white" data-testid="advisor-login-page">
      {/* Left panel — Premium Emerald Marketing */}
      <div className="hidden lg:flex lg:w-[52%] bg-emerald-950 text-white flex-col justify-between p-14 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center mix-blend-overlay"
          style={BG_STYLE}
        />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 -left-20 w-80 h-80 bg-emerald-400/10 rounded-full blur-3xl" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-20">
            <div className="w-11 h-11 bg-emerald-500 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-900/40">
              <Calculator className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>ivoliq</span>
            <span className="text-[10px] text-emerald-300 border border-emerald-700/60 rounded-full px-2.5 py-1 ml-1 uppercase tracking-[0.2em]">Partner-Portal</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Schluss mit<br />Belegchaos und<br /><span className="text-emerald-400">Excel-Listen.</span>
          </h1>
          <p className="text-emerald-100/70 text-lg max-w-md leading-relaxed">
            Ihre Mandanten nutzen ivoliq. Sie profitieren mit — GoBD-konforme Daten, in Echtzeit.
          </p>
        </div>
        <div className="relative z-10 grid grid-cols-3 gap-4 mt-12">
          {[
            { icon: Users, label: 'Live-Daten', desc: 'Einnahmen, Ausgaben & Belege in Echtzeit' },
            { icon: FileText, label: 'DATEV / BMD', desc: 'Buchungsstapel per Klick exportieren' },
            { icon: Shield, label: 'Kollaboration', desc: 'Rückfragen & Aufgaben direkt am Beleg' },
          ].map(({ icon: Icon, label, desc }) => (
            <div key={label} className="rounded-2xl border border-emerald-800/50 bg-emerald-900/30 backdrop-blur-sm p-4 transition-colors hover:bg-emerald-900/50">
              <Icon className="w-5 h-5 text-emerald-400 mb-3" strokeWidth={1.5} />
              <div className="text-sm font-medium mb-1">{label}</div>
              <div className="text-xs text-emerald-100/50 leading-relaxed">{desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-8 sm:p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 bg-emerald-600 rounded-xl flex items-center justify-center">
              <Calculator className="h-5 w-5 text-white" />
            </div>
            <span className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>ivoliq</span>
            <span className="text-[10px] text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5 uppercase tracking-widest">Partner</span>
          </div>

          {authLoading ? (
            <div className="py-24 flex items-center justify-center" data-testid="advisor-login-checking">
              <Loader2 className="h-6 w-6 text-emerald-600 animate-spin" />
            </div>
          ) : (
          <>
          <h2 className="text-3xl font-semibold text-slate-900 mb-1.5 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {mode === 'login' ? 'Willkommen zurück' : 'Konto erstellen'}
          </h2>
          <p className="text-sm text-slate-500 mb-7">
            {mode === 'login' ? 'Melden Sie sich in Ihrem Steuerberater-Portal an.' : 'Erstellen Sie Ihr Konto und verwalten Sie Ihre Mandanten.'}
          </p>

          {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm p-3.5 rounded-xl mb-4" data-testid="advisor-auth-error">{error}</div>}
          {success && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm p-3.5 rounded-xl mb-4 flex items-start gap-2" data-testid="advisor-auth-success"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />{success}</div>}

          {step === 'pwchange' && (
            <form onSubmit={handleForcePwChange} className="space-y-4" data-testid="advisor-pwchange-form">
              <p className="text-sm text-slate-600"><span className="font-semibold text-slate-900">Erstanmeldung:</span> Bitte vergeben Sie jetzt ein eigenes Passwort (mind. 12 Zeichen).</p>
              <div><Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Neues Passwort *</Label>
                <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} minLength={12} required className={inputCls} data-testid="advisor-newpassword-input" /></div>
              <div><Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Neues Passwort wiederholen *</Label>
                <Input type="password" value={newPassword2} onChange={e => setNewPassword2(e.target.value)} minLength={12} required className={inputCls} data-testid="advisor-newpassword2-input" /></div>
              <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-12 text-sm font-semibold shadow-sm transition-colors" data-testid="advisor-pwchange-submit">
                {loading ? 'Wird verarbeitet...' : 'Passwort setzen & anmelden'}
              </Button>
            </form>
          )}

          {step === 'mfa' && (
            <form onSubmit={handleMfaVerify} className="space-y-4" data-testid="advisor-mfa-form">
              <p className="text-sm text-slate-600"><span className="font-semibold text-slate-900">Zwei-Faktor-Bestätigung:</span> Code Ihrer Authenticator-App oder Recovery-Code eingeben.</p>
              <Input autoFocus inputMode="numeric" autoComplete="one-time-code" value={mfaCode}
                onChange={e => setMfaCode(e.target.value.trim())} placeholder="123456" required className={`${inputCls} text-center tracking-[0.4em]`} data-testid="advisor-mfa-code-input" />
              <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-12 text-sm font-semibold shadow-sm transition-colors" data-testid="advisor-mfa-submit">
                {loading ? 'Wird verarbeitet...' : 'Bestätigen'}
              </Button>
              <button type="button" className="text-xs text-slate-400 underline w-full" onClick={() => { setStep('form'); setMfaCode(''); }} data-testid="advisor-mfa-back">Zurück zur Anmeldung</button>
            </form>
          )}

          {step === 'form' && (
          <form onSubmit={mode === 'login' ? handleLogin : handleRegister} className="space-y-4">
            {mode === 'register' && (
              <>
                <div><Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Name *</Label>
                  <Input value={name} onChange={e => setName(e.target.value)} placeholder="Max Mustermann" required className={inputCls} data-testid="advisor-name-input" /></div>
                <div><Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Kanzlei / Firma</Label>
                  <Input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Mustermann Steuerberatung" className={inputCls} data-testid="advisor-company-input" /></div>
                <div><Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Steuernummer</Label>
                  <Input value={taxNumber} onChange={e => setTaxNumber(e.target.value)} placeholder="DE123456789 / ATU12345678" className={inputCls} data-testid="advisor-taxnumber-input" /></div>
              </>
            )}
            <div><Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">E-Mail *</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="berater@kanzlei.de" required className={inputCls} data-testid="advisor-email-input" /></div>
            <div><Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Passwort *</Label>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required className={inputCls} data-testid="advisor-password-input" /></div>
            <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-12 text-sm font-semibold shadow-sm transition-colors" data-testid="advisor-submit-button">
              {loading ? 'Wird verarbeitet...' : <>{mode === 'login' ? 'Einloggen' : 'Konto erstellen'}<ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>
          )}

          {step === 'form' && (
          <div className="mt-7 text-center space-y-3">
            {mode === 'login' ? (
              <p className="text-sm text-slate-500">
                Noch kein Konto?{' '}
                <button onClick={() => { setMode('register'); setError(''); setSuccess(''); }} className="text-emerald-700 font-semibold hover:underline" data-testid="switch-to-register">
                  Jetzt registrieren
                </button>
              </p>
            ) : (
              <p className="text-sm text-slate-500">
                Bereits registriert?{' '}
                <button onClick={() => { setMode('login'); setError(''); setSuccess(''); }} className="text-emerald-700 font-semibold hover:underline" data-testid="switch-to-login">
                  Anmelden
                </button>
              </p>
            )}
            <p className="text-xs text-slate-400">
              Sie sind Freelancer?{' '}<a href="/login" className="text-slate-600 hover:text-emerald-700 font-medium underline">Hier anmelden</a>
            </p>
          </div>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}
